import { useUser } from "@/hooks/useUser";
import { getCurrentOrgRole } from "@/utils/roleUtils";
import {
  canManageProjectTasksAndContent,
  normalizeProjectScope,
} from "@/utils/projectRoleAccess";
import { UserRoleEnumValue } from "@backend/src/schemas/enums";
import type { Task } from "@/api/mutation/useTaskCreateMutation";
import type { UserType } from "@/api/query/useCurrentUserQuery";
import type { ProjectDetailType } from "@/api/query/useProjectDetailQuery";

interface UseTaskPermissionsParams {
  taskId?: string;
  project?: ProjectDetailType;
  task?: Task;
}

export function useTaskPermissions({
  taskId,
  project,
  task,
}: UseTaskPermissionsParams) {
  const user = useUser();
  const me = user.user;

  const isAllowedToEdit = (): boolean => {
    if (!taskId) {
      return true;
    }
    if (!me || !project) {
      return false;
    }

    const assignedUsers = project.assignedUsers ?? [];

    if (
      canManageProjectTasksAndContent(
        me as UserType,
        getCurrentOrgRole(me) as Parameters<typeof canManageProjectTasksAndContent>[1],
        assignedUsers,
        normalizeProjectScope(project),
      )
    ) {
      return true;
    }

    if (
      assignedUsers.some(
        (u) =>
          u.user.userId === me.userId &&
          u.projectRole === UserRoleEnumValue.COLLABORATOR,
      )
    ) {
      return false;
    }

    if (
      task?.assignedUsers?.some((u) => u.user.userId === me.userId) ||
      task?.createdByUserId === me.userId
    ) {
      return true;
    }

    return false;
  };

  return {
    allowed: isAllowedToEdit(),
    currentUser: me,
  };
}

export default useTaskPermissions;
